import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la integración con Strava.
 *
 * Nota: reutilizamos `fetch` directamente (con `credentials: 'include'`) en
 * lugar del helper `request` de `./api` porque los endpoints de Strava tienen
 * un mapeo de errores muy específico (409 = no conectado, 503 con varias
 * causas, etc.) que no encaja con las excepciones genéricas de `api.ts`.
 *
 * El flujo OAuth no se hace por fetch — necesita una navegación real del
 * navegador porque hay redirect cross-site a `strava.com`. Usar
 * `startStravaOAuth()` (o equivalentemente `window.location.href = ...`).
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Errores específicos de Strava
// ---------------------------------------------------------------------------

/** Backend no tiene `STRAVA_CLIENT_ID` / `STRAVA_CLIENT_SECRET` configurados. */
export class StravaNotConfiguredError extends ApiError {
  public readonly hint: string;
  constructor(hint: string) {
    super('Strava no configurado en el backend', 503);
    this.name = 'StravaNotConfiguredError';
    this.hint = hint;
  }
}

/** Strava rechazó el token (401). El frontend debe forzar reconexión. */
export class StravaUnauthorizedError extends ApiError {
  constructor() {
    super('strava_unauthorized', 401);
    this.name = 'StravaUnauthorizedError';
  }
}

/** Strava devolvió rate limit (503 con `strava_rate_limited`). */
export class StravaRateLimitedError extends ApiError {
  public readonly retryAfterSeconds: number | null;
  constructor(retryAfterSeconds: number | null) {
    super('strava_rate_limited', 503);
    this.name = 'StravaRateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Strava devolvió 5xx (502 desde nuestro backend). */
export class StravaUpstreamError extends ApiError {
  constructor() {
    super('strava 5xx', 502);
    this.name = 'StravaUpstreamError';
  }
}

/** Usuario no tiene Strava conectado al pedir sync (409). */
export class StravaNotConnectedError extends ApiError {
  constructor() {
    super('no conectado', 409);
    this.name = 'StravaNotConnectedError';
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const StravaStatusSchema = z.object({
  // `appConfigured` se introduce en M1.9 — opcional para retrocompat con
  // backends antiguos (asumimos `false` si falta, así la UI fuerza al usuario
  // a configurar las claves antes de poder conectar).
  appConfigured: z.boolean().optional(),
  connected: z.boolean(),
  athleteId: z.number().int().optional(),
  athleteName: z.string().optional(),
  lastSyncAt: z.string().nullable().optional(),
  lastSyncCount: z.number().int().nonnegative().nullable().optional(),
  expiresAt: z.string().optional(),
  scopes: z.string().optional(),
});
export type StravaStatus = z.infer<typeof StravaStatusSchema>;

export const StravaSyncResponseSchema = z.object({
  syncedCount: z.number().int().nonnegative(),
  lastSyncAt: z.string(),
});
export type StravaSyncResponse = z.infer<typeof StravaSyncResponseSchema>;

export const StravaSyncRequestSchema = z.object({
  sinceISO: z.string().optional(),
});
export type StravaSyncRequest = z.infer<typeof StravaSyncRequestSchema>;

// ---------------------------------------------------------------------------
// App config (M1.9) — claves Strava por profile, guardadas cifradas en BD
// ---------------------------------------------------------------------------

/**
 * Respuesta de `GET /api/strava/app-config`. Si `configured: false` el resto
 * de campos relativos a la app (clientId, updatedAt) no vienen, pero
 * `callbackUrl` sí (la UI lo muestra siempre para que el usuario lo pegue en
 * la app de Strava como "Authorization Callback Domain").
 *
 * NUNCA incluye `clientSecret` — sólo el clientId, que es público.
 */
export const StravaAppConfigSchema = z.object({
  configured: z.boolean(),
  clientId: z.string().optional(),
  callbackUrl: z.string().url(),
  updatedAt: z.string().optional(),
});
export type StravaAppConfig = z.infer<typeof StravaAppConfigSchema>;

/**
 * Body para `PUT /api/strava/app-config`. Reglas validadas por el backend con
 * Zod (1-50 chars numéricos, 32-100 chars alfanuméricos respectivamente);
 * replicamos la validación principal aquí para feedback inmediato en la UI.
 */
export const StravaAppConfigPutBody = z.object({
  clientId: z
    .string()
    .min(1, 'Obligatorio')
    .max(50, 'Máximo 50 caracteres')
    .regex(/^\d+$/, 'Strava usa Client IDs numéricos'),
  clientSecret: z
    .string()
    .min(32, 'Mínimo 32 caracteres')
    .max(100, 'Máximo 100 caracteres')
    .regex(/^[a-zA-Z0-9]+$/, 'Sólo letras y números'),
});
export type StravaAppConfigPutBody = z.infer<typeof StravaAppConfigPutBody>;

/**
 * Respuesta de `PUT /api/strava/app-config`. Es el mismo shape que el GET más
 * `previousConnectionInvalidated`: true cuando el upsert eliminó tokens
 * OAuth previos porque se cambió el clientSecret y los tokens viejos ya no
 * sirven.
 */
export const StravaAppConfigPutResponseSchema = StravaAppConfigSchema.extend({
  previousConnectionInvalidated: z.boolean().optional(),
});
export type StravaAppConfigPutResponse = z.infer<typeof StravaAppConfigPutResponseSchema>;

/**
 * Error de validación 400 del backend: `{ error, issues: { fieldErrors, formErrors } }`
 * tal y como devuelve `z.flatten()`. Lo modelamos aquí para que el form pueda
 * pintar `issues.fieldErrors.clientId[0]` directamente.
 */
export class StravaAppConfigValidationError extends ApiError {
  public readonly fieldErrors: Record<string, string[]>;
  public readonly formErrors: string[];
  constructor(fieldErrors: Record<string, string[]>, formErrors: string[]) {
    super('Validación rechazada por el backend', 400);
    this.name = 'StravaAppConfigValidationError';
    this.fieldErrors = fieldErrors;
    this.formErrors = formErrors;
  }
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function pickNumber(body: unknown, key: string): number | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function getStravaStatus(): Promise<StravaStatus> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/status`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = StravaStatusSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError('Respuesta /api/strava/status no cumple esquema', response.status, parsed.error);
  }
  return parsed.data;
}

export async function syncStrava(body?: StravaSyncRequest): Promise<StravaSyncResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/sync`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = StravaSyncResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError('Respuesta /api/strava/sync no cumple esquema', 200, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  const errStr = pickString(json, 'error') ?? '';

  if (response.status === 409) {
    throw new StravaNotConnectedError();
  }
  if (response.status === 401 || errStr === 'strava_unauthorized') {
    throw new StravaUnauthorizedError();
  }
  if (response.status === 503) {
    if (errStr === 'strava_rate_limited') {
      const retryAfter =
        pickNumber(json, 'retryAfter') ??
        (Number.parseInt(response.headers.get('Retry-After') ?? '', 10) || null);
      throw new StravaRateLimitedError(Number.isFinite(retryAfter as number) ? (retryAfter as number) : null);
    }
    if (errStr === 'strava no configurado' || errStr.includes('configurado')) {
      const hint = pickString(json, 'hint') ?? '';
      throw new StravaNotConfiguredError(hint);
    }
  }
  if (response.status === 502) {
    throw new StravaUpstreamError();
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function getStravaAppConfig(): Promise<StravaAppConfig> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/app-config`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = StravaAppConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/strava/app-config no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data;
}

export async function putStravaAppConfig(
  body: StravaAppConfigPutBody,
): Promise<StravaAppConfigPutResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/app-config`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = StravaAppConfigPutResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/strava/app-config (PUT) no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }

  if (response.status === 400) {
    const json = await readJsonSafe(response);
    const issues =
      json && typeof json === 'object' && 'issues' in json
        ? (json as { issues?: unknown }).issues
        : null;
    const fieldErrors: Record<string, string[]> = {};
    const formErrors: string[] = [];
    if (issues && typeof issues === 'object') {
      const i = issues as { fieldErrors?: unknown; formErrors?: unknown };
      if (i.fieldErrors && typeof i.fieldErrors === 'object') {
        for (const [k, v] of Object.entries(i.fieldErrors as Record<string, unknown>)) {
          if (Array.isArray(v)) {
            fieldErrors[k] = v.filter((x): x is string => typeof x === 'string');
          }
        }
      }
      if (Array.isArray(i.formErrors)) {
        for (const v of i.formErrors) {
          if (typeof v === 'string') formErrors.push(v);
        }
      }
    }
    throw new StravaAppConfigValidationError(fieldErrors, formErrors);
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function deleteStravaAppConfig(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/app-config`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  // 204 esperado; 200 también lo aceptamos por defensa. Idempotente: si ya no
  // había nada el backend también devuelve 204.
  if (response.status === 204 || response.ok) return;
  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function disconnectStrava(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/strava/disconnect`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 204 || response.ok) return;
  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

/**
 * Inicia el flujo OAuth de Strava. NO es una llamada XHR: navega de verdad,
 * porque el backend hace `302 → strava.com` y los redirects cross-site no
 * funcionan vía `fetch`.
 */
export function startStravaOAuth(): void {
  // Asignación a `location.href` (no `location.assign(...)`) — en jsdom moderno
  // `Location.assign` no es redefinible/spyable sin reemplazar `location` entero,
  // mientras que `href = ...` se puede leer en tests tras el click.
  window.location.href = '/api/strava/oauth/start';
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const STRAVA_STATUS_QUERY_KEY = ['strava', 'status'] as const;
export const STRAVA_APP_CONFIG_QUERY_KEY = ['strava', 'app-config'] as const;

export function useStravaStatus(): UseQueryResult<StravaStatus, Error> {
  return useQuery<StravaStatus, Error>({
    queryKey: STRAVA_STATUS_QUERY_KEY,
    queryFn: getStravaStatus,
    staleTime: 0,
    retry: 1,
  });
}

export function useStravaAppConfig(): UseQueryResult<StravaAppConfig, Error> {
  return useQuery<StravaAppConfig, Error>({
    queryKey: STRAVA_APP_CONFIG_QUERY_KEY,
    queryFn: getStravaAppConfig,
    staleTime: 0,
    retry: 1,
  });
}

export function useUpdateStravaAppConfig(): UseMutationResult<
  StravaAppConfigPutResponse,
  Error,
  StravaAppConfigPutBody
> {
  const qc = useQueryClient();
  return useMutation<StravaAppConfigPutResponse, Error, StravaAppConfigPutBody>({
    mutationFn: (body) => putStravaAppConfig(body),
    onSuccess: async () => {
      // Cambia tanto la propia app-config como el status (que ahora incluye
      // `appConfigured`); si se invalidó conexión previa también afecta a
      // `connected`/`athleteName`, así que invalidar status es obligatorio.
      await qc.invalidateQueries({ queryKey: STRAVA_APP_CONFIG_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: STRAVA_STATUS_QUERY_KEY });
    },
  });
}

export function useDeleteStravaAppConfig(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteStravaAppConfig(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: STRAVA_APP_CONFIG_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: STRAVA_STATUS_QUERY_KEY });
    },
  });
}

export function useStravaSync(): UseMutationResult<
  StravaSyncResponse,
  Error,
  StravaSyncRequest | undefined
> {
  const qc = useQueryClient();
  return useMutation<StravaSyncResponse, Error, StravaSyncRequest | undefined>({
    mutationFn: (body) => syncStrava(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: STRAVA_STATUS_QUERY_KEY });
      // `['workouts']` aún no existe (llega en M1.3/M1.5) — invalidamos por
      // hábito para que la primera vez que se introduzca ya quede en sintonía
      // con cada sync.
      await qc.invalidateQueries({ queryKey: ['workouts'] });
    },
  });
}

export function useStravaDisconnect(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => disconnectStrava(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: STRAVA_STATUS_QUERY_KEY });
    },
  });
}
